import { useLocation, useNavigate } from "react-router-dom";
import { useT } from "@/i18n";
import { cn } from "@/lib/utils";
import { closesByGoingBack, coldBackgroundPath, type DetailLocationState } from "@/app/detail-navigation";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "./dialog";
import { Tooltip } from "./tooltip";

/**
 * The shell every detail wears.
 *
 * Ported from bd-admin's dialog arrangement so the agency's apps read the same way: a
 * full-bleed tinted header block carrying the record's identity, and the content beneath it
 * in its own padded body. The tint is `bg-muted/50` — bd-admin's "coloured" header is a
 * muted wash rather than a brand colour, and manager colours re-skin it later.
 *
 * **Close means Back.** Every detail is a route, so dismissing one is a navigation, not a
 * state change: Escape, the close button and the backdrop all step back through history.
 * That is what makes an opportunity opened from inside a client modal close *to* that
 * client modal, and a detail opened from a filtered board close back to the board with its
 * filters intact — the surface underneath is the previous history entry, which still holds
 * its own URL.
 *
 * Opened cold from a pasted link there is nothing behind, so close replaces the entry with
 * the surface the record belongs to rather than leaving the browser nowhere to go.
 */
export function DetailModal({
  title,
  description,
  header,
  actions,
  children,
  wide = false,
}: {
  /** The accessible name. Rendered in the header unless `header` replaces it. */
  title: string;
  description?: string;
  /** The header block's content, drawn on the tint. Falls back to the title alone. */
  header?: React.ReactNode;
  /**
   * The record's actions, as icon buttons at the top-right of the header block, immediately
   * left of the close ✕.
   *
   * A slot rather than a row inside `header` so every detail places its actions in the same
   * place, and so the reserved space next to the close button is the shell's business rather
   * than each modal's. Details that have no actions pass nothing and are unchanged.
   */
  actions?: React.ReactNode;
  children: React.ReactNode;
  /** For a body carrying a table — the client's opportunity list. */
  wide?: boolean;
  /**
   * The owner's palette key, which tints the header block.
   *
   * This is what makes the header the board's colour legend: open a card and see whose
   * colour that was. Absent — an unassigned owner, or the client modal, which belongs to no
   * manager — it keeps the muted tint it shipped with.
   */
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as DetailLocationState;

  const close = (): void => {
    if (closesByGoingBack(state)) {
      navigate(-1);
      return;
    }
    // A cold load has no history entry to step into; replacing means the browser's Back
    // still leaves the app rather than bouncing between the modal and itself.
    void navigate(coldBackgroundPath(location.pathname), { replace: true });
  };

  return (
    <Dialog open onOpenChange={(next) => !next && close()}>
      {/* No explicit `aria-labelledby`: passing it as `undefined` **overrides** the id Radix
          wires from `DialogTitle`, and the dialog ends up with no accessible name at all —
          which is what a screen reader announces when a modal opens. Radix's own wiring is
          correct here, including when the visible title lives inside `header`. */}
      <DialogContent
        className={cn("max-h-[85vh] gap-0 overflow-y-auto p-0", wide ? "sm:max-w-3xl" : "sm:max-w-2xl")}
      >
        <div
          className={cn(
            "relative rounded-t-lg border-b px-6 py-4",
            // Only the close ✕ needs guaranteed clearance on the whole block — it is
            // `top-4 right-4` regardless of what `header` renders. The actions sit in the
            // same corner but only overlap whichever row of `header` is level with them, so
            // *that* row carries its own extra padding (see the identity row in
            // OpportunityModal) rather than the padding being levied on this whole block.
            // A four-column facts grid several rows further down was losing 10rem of width
            // it never needed to a squeeze meant for the title line above it.
            "pr-12",
            "detail-header",
            // A **container**, so the blocks inside can lay themselves out against the
            // dialog's width instead of the window's. `sm:`/`lg:` inside a fixed-width modal
            // ask the wrong question: at 1440px the viewport says "wide" while the column a
            // fact actually gets is 130px, which is how the hero's five columns came to
            // overlap each other's values.
            "@container",
          )}
        >
          {/* Immediately left of the ✕, which Radix draws at `top-4 right-4`. */}
          {actions && <div className="absolute top-3.5 right-12 flex items-center gap-1">{actions}</div>}

          {header ?? <DialogTitle className="text-xl">{title}</DialogTitle>}
          {/* Radix requires a title for the accessible name; when `header` supplies its own
              visible one, this keeps the dialog named without rendering twice. */}
          {header && (
            <DialogTitle className="sr-only">{title}</DialogTitle>
          )}
          {description ? (
            <DialogDescription className="mt-1">{description}</DialogDescription>
          ) : (
            <DialogDescription className="sr-only">{title}</DialogDescription>
          )}
        </div>

        <div className="p-6">{children}</div>
      </DialogContent>
    </Dialog>
  );
}

/** A labelled fact in a detail's header or facts strip. */
export function DetailFact({
  label,
  children,
  className,
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("min-w-0", className)}>
      <dt className="text-muted-foreground text-xs">{label}</dt>
      <dd className="truncate text-sm font-medium">{children}</dd>
    </div>
  );
}

/**
 * A record action, as an icon with a tooltip and an accessible name.
 *
 * Icons rather than labelled buttons because they sit in the header's top-right corner beside
 * the close ✕, where a row of words would compete with the record's identity. The name is on
 * `aria-label`, so nothing about them is mouse-only, and the tooltip repeats it for a sighted
 * reader asking what a glyph means.
 *
 * It lives here, beside the `actions` slot it is made for, rather than in one detail screen
 * (2026-08-26: the client detail needed the same thing, and a second copy is how two details
 * start disagreeing about what an action looks like).
 */
export function DetailAction({
  label,
  destructive = false,
  disabled = false,
  onClick,
  children,
}: {
  label: string;
  destructive?: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  const t = useT();

  return (
    <Tooltip content={disabled ? t("common.paused") : label}>
      <button
        type="button"
        aria-label={label}
        disabled={disabled}
        onClick={onClick}
        className={cn(
          "hover:bg-accent focus-visible:ring-ring rounded-sm p-1.5 focus-visible:ring-2 focus-visible:outline-none disabled:opacity-50",
          destructive && "text-destructive hover:bg-destructive/10",
        )}
      >
        {children}
      </button>
    </Tooltip>
  );
}
